import { Icon } from './Icons';
import React, { useState, useEffect, useRef } from 'react';
import { saveUserProfile, loadLatestMetrics } from '../db';
import { auth } from '../firebase';
import { sendPasswordResetEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { Field, Select, TextArea } from './FormFields';

function calcAge(dob) {
  if (!dob) return null;
  return Math.floor((Date.now() - new Date(dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
}

function calcCompletion(profile) {
  const fields = ['firstName', 'lastName', 'dob', 'gender', 'jobTitle', 'smokingStatus', 'alcoholUnits', 'medicalConditions'];
  const filled = fields.filter(f => profile[f] && profile[f] !== '').length;
  return Math.round((filled / fields.length) * 100);
}

function Avatar({ name, photoURL, size = 72, onClick, uploading }) {
  const initials = name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
  return (
    <div onClick={onClick} style={{
      width: size, height: size, borderRadius: '50%',
      background: 'rgba(0,174,239,0.15)',
      border: '2px solid rgba(0,174,239,0.3)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Playfair Display', serif",
      fontSize: size * 0.35 + 'px', fontWeight: 500, color: '#00AEEF',
      flexShrink: 0, cursor: onClick ? 'pointer' : 'default',
      overflow: 'hidden', position: 'relative'
    }}>
      {photoURL ? (
        <img src={photoURL} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        initials
      )}
      {uploading && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 20, height: 20, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      )}
      {!photoURL && onClick && !uploading && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', padding: '3px', display: 'flex', justifyContent: 'center' }}>
          <Icon name="camera" size={11} color="#fff" />
        </div>
      )}
    </div>
  );
}

function SectionHeader({ label, icon }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '1.1rem' }}>
      <Icon name={icon} size={14} color="var(--text3)" />
      <div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>{label}</div>
    </div>
  );
}

export default function UserProfile() {
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [pwError, setPwError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [units, setUnits] = React.useState('metric'); // 'metric' | 'imperial'
  const [photoURL, setPhotoURL] = React.useState(null);
  const [photoUploading, setPhotoUploading] = React.useState(false);
  const [profile, setProfile] = useState({
    firstName: '', lastName: '', dob: '', gender: '', jobTitle: '',
    department: '', companyName: '', ethnicity: '', smokingStatus: '',
    alcoholUnits: '', medicalConditions: '', medications: '', familyHistory: '',
    units: 'metric'
  });

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const p = await loadLatestMetrics('profile');
        if (p) {
          setProfile(prev => ({ ...prev, ...p }));
          if (p.units) setUnits(p.units);
          if (p.photoURL) setPhotoURL(p.photoURL);
        }
        const user = auth.currentUser;
        if (user?.displayName && !p?.firstName) {
          const parts = user.displayName.split(' ');
          setProfile(prev => ({ ...prev, firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' }));
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, []);

  async function handleSave() {
    setSaving(true); setSaved(false);
    try {
      await saveUserProfile({ ...profile, units });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert('Error saving profile.'); }
    setSaving(false);
  }

  const set = key => v => setProfile(p => ({ ...p, [key]: v }));

  const fileInputRef = useRef(null);

  async function handlePhotoUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Photo must be under 2MB'); return; }
    setPhotoUploading(true);
    try {
      // Convert to base64 data URL and store in Firestore
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const dataURL = ev.target.result;
        setPhotoURL(dataURL);
        await saveUserProfile({ ...profile, units, photoURL: dataURL });
        setPhotoUploading(false);
      };
      reader.readAsDataURL(file);
    } catch(e) {
      console.error(e);
      setPhotoUploading(false);
    }
  }

  // Unit conversion helpers
  function displayWeight(kg) {
    if (!kg) return '';
    if (units === 'imperial') return (parseFloat(kg) * 2.20462).toFixed(1);
    return kg;
  }
  function displayHeight(cm) {
    if (!cm) return '';
    if (units === 'imperial') {
      const totalInches = parseFloat(cm) / 2.54;
      const ft = Math.floor(totalInches / 12);
      const inch = Math.round(totalInches % 12);
      return ft + "'" + inch + '"';
    }
    return cm;
  }

  const fullName = [profile.firstName, profile.lastName].filter(Boolean).join(' ');
  const age = calcAge(profile.dob);
  const completion = calcCompletion(profile);

  if (loading) return (
    <div style={{ padding: '2rem 0' }}>
      <div style={{ width: '160px', height: '28px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', marginBottom: '.5rem' }} />
      <div style={{ width: '260px', height: '14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)' }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>

      {/* ── HEADER ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em', marginBottom: '.2rem' }}>My Profile</h1>
          <p style={{ fontSize: '.77rem', color: 'var(--text3)', fontWeight: 300 }}>Helps Rico give you more accurate, personalised advice</p>
        </div>
        <button onClick={handleSave} disabled={saving} style={{
          padding: '.6rem 1.4rem', borderRadius: 'var(--radius)', border: 'none',
          background: saved ? 'var(--success)' : 'var(--primary)',
          color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.82rem',
          fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? .7 : 1, transition: 'all .2s', whiteSpace: 'nowrap', flexShrink: 0
        }}>
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Profile'}
        </button>
      </div>

      {/* ── PROFILE CARD ── */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
        <Avatar name={fullName || 'User'} photoURL={photoURL} size={72} uploading={photoUploading} onClick={() => fileInputRef.current?.click()} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.3rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.2rem' }}>
            {fullName || 'Your Name'}
          </div>
          <div style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, marginBottom: '.75rem' }}>
            {[age ? age + ' years old' : null, profile.gender ? profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1) : null, profile.jobTitle || null].filter(Boolean).join(' · ') || 'Complete your profile below'}
          </div>
          {/* Completion bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 100, maxWidth: 200, height: 5, background: 'var(--bg-elevated)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: completion + '%', height: '100%', background: completion >= 80 ? 'var(--success)' : completion >= 50 ? 'var(--primary)' : 'var(--warning)', borderRadius: 3, transition: 'width .8s ease' }} />
            </div>
            <span style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 400, whiteSpace: 'nowrap' }}>{completion}% complete</span>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
        {completion < 100 && (
          <div style={{ padding: '.6rem 1rem', background: 'rgba(0,174,239,0.06)', border: '1px solid rgba(0,174,239,0.15)', borderRadius: 'var(--radius)', fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.5, maxWidth: 220 }}>
            A complete profile helps Rico give you significantly more accurate and personalised health advice.
          </div>
        )}
      </div>

      {/* ── FORM SECTIONS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>

        {/* Personal Details */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
          <SectionHeader label="Personal Details" icon="user" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
            <Field label="First Name" value={profile.firstName} onChange={set('firstName')} placeholder="Alex" />
            <Field label="Last Name" value={profile.lastName} onChange={set('lastName')} placeholder="Johnson" />
          </div>
          <Field label="Date of Birth" type="date" value={profile.dob} onChange={set('dob')} />
          <Select label="Gender" value={profile.gender} onChange={set('gender')} options={[
            { value: 'male', label: 'Male' },
            { value: 'female', label: 'Female' }
          ]} />
          <Select label="Ethnicity" value={profile.ethnicity} onChange={set('ethnicity')} options={[
            { value: 'white-british', label: 'White British' },
            { value: 'white-other', label: 'White Other' },
            { value: 'mixed', label: 'Mixed / Multiple' },
            { value: 'asian-british', label: 'Asian / Asian British' },
            { value: 'black-british', label: 'Black / Black British' },
            { value: 'other', label: 'Other' },
            { value: 'prefer-not-to-say', label: 'Prefer not to say' }
          ]} />
        </div>

        {/* Work & Lifestyle */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
          <SectionHeader label="Work Details" icon="briefcase" />
          <Field label="Job Title" value={profile.jobTitle} onChange={set('jobTitle')} placeholder="e.g. Senior Manager" />
          <Field label="Department" value={profile.department} onChange={set('department')} placeholder="e.g. Finance" />
          <Field label="Company Name" value={profile.companyName} onChange={set('companyName')} placeholder="e.g. Acme Corp" />

          <div style={{ borderTop: '0.5px solid var(--border2)', marginTop: '1.25rem', paddingTop: '1.25rem' }}>
            <SectionHeader label="Lifestyle" icon="leaf" />
            <Select label="Smoking Status" value={profile.smokingStatus} onChange={set('smokingStatus')} options={[
              { value: 'never', label: 'Never smoked' },
              { value: 'ex', label: 'Ex-smoker' },
              { value: 'occasional', label: 'Occasional smoker' },
              { value: 'regular', label: 'Regular smoker' }
            ]} />
            <Field label="Weekly Alcohol Units" type="number" value={profile.alcoholUnits} onChange={set('alcoholUnits')} placeholder="e.g. 6" />
          </div>
        </div>
      </div>

      {/* Units Preference */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '1rem' }}>
          <Icon name="sliders" size={14} color="var(--text3)" />
          <div style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em' }}>Units Preference</div>
        </div>
        <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
          {[
            { id: 'metric', label: 'Metric', sub: 'kg, cm, mmol/L' },
            { id: 'imperial', label: 'Imperial', sub: 'lbs, ft/in' },
          ].map(u => (
            <button key={u.id} onClick={() => { setUnits(u.id); setProfile(p => ({ ...p, units: u.id })); }}
              style={{ padding: '.65rem 1.25rem', borderRadius: 'var(--radius)', border: '1px solid', flexShrink: 0,
                borderColor: units === u.id ? 'var(--primary)' : 'var(--border2)',
                background: units === u.id ? 'rgba(0,174,239,0.08)' : 'transparent',
                cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'left' }}>
              <div style={{ fontSize: '.82rem', fontWeight: 500, color: units === u.id ? 'var(--primary)' : 'var(--text)' }}>{u.label}</div>
              <div style={{ fontSize: '.7rem', color: 'var(--text3)', marginTop: '.15rem' }}>{u.sub}</div>
            </button>
          ))}
        </div>
        <p style={{ fontSize: '.72rem', color: 'var(--text3)', marginTop: '.75rem', fontWeight: 300 }}>
          This affects how weight, height and distances are displayed across the platform. All data is stored in metric internally.
        </p>
      </div>

      {/* Medical History — full width */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', marginBottom: '1rem' }}>
        <SectionHeader label="Medical History" icon="stethoscope" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          <TextArea label="Existing Medical Conditions" value={profile.medicalConditions} onChange={set('medicalConditions')} placeholder="e.g. Type 2 diabetes, hypertension…" rows={3} />
          <TextArea label="Current Medications" value={profile.medications} onChange={set('medications')} placeholder="e.g. Metformin 500mg, Lisinopril 10mg…" rows={3} />
          <TextArea label="Family Medical History" value={profile.familyHistory} onChange={set('familyHistory')} placeholder="e.g. Father had heart disease, mother has T2 diabetes…" rows={3} />
        </div>
        <div style={{ padding: '.75rem 1rem', background: 'rgba(0,174,239,0.04)', border: '1px solid rgba(0,174,239,0.12)', borderRadius: 'var(--radius)', fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
          <Icon name="lock" size={12} style={{ display: "inline", marginRight: 4 }} /> Your medical history is used by Rico to give contextually accurate advice. It is stored securely and is never shared without your consent.
        </div>
      </div>

      {/* Why this matters */}
      <div style={{ background: 'rgba(0,174,239,0.04)', border: '1px solid rgba(0,174,239,0.12)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        <div style={{ fontSize: '1.2rem', flexShrink: 0 }}><Icon name="info" size={14} /></div>
        <div>
          <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.3rem' }}>Why your profile matters</div>
          <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
            Rico uses your age, gender, ethnicity, lifestyle and medical history to contextualise your blood results. For example, optimal cholesterol ranges differ by gender and family history changes cardiovascular risk calculations significantly. The more complete your profile, the more accurate Rico's advice becomes.
          </p>
        </div>
      </div>

      {/* Mobile save button */}
      <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={handleSave} disabled={saving} style={{
          padding: '.75rem 2.5rem', borderRadius: 'var(--radius)', border: 'none',
          background: saved ? 'var(--success)' : 'var(--primary)',
          color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.85rem',
          fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer',
          opacity: saving ? .7 : 1, transition: 'all .2s', width: '100%', maxWidth: 300
        }}>
          {saving ? 'Saving…' : saved ? '✓ Profile Saved' : 'Save Profile'}
        </button>
      </div>

      {/* Password change */}
      <div style={{ marginTop: '2rem', background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.25rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showPasswordChange ? '1rem' : 0 }}>
          <div>
            <div style={{ fontSize: '.82rem', fontWeight: 500, color: 'var(--text)', marginBottom: '.2rem' }}>Password</div>
            <div style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>Change your account password</div>
          </div>
          <button onClick={() => { setShowPasswordChange(!showPasswordChange); setPwMsg(''); setPwError(''); }}
            style={{ padding: '.5rem 1rem', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'transparent', color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontSize: '.78rem', cursor: 'pointer' }}>
            {showPasswordChange ? 'Cancel' : 'Change Password'}
          </button>
        </div>
        {showPasswordChange && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
            <div>
              <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>Current Password</label>
              <input type="password" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} placeholder="Enter current password"
                style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.6rem .85rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>New Password</label>
              <input type="password" value={pwNew} onChange={e => setPwNew(e.target.value)} placeholder="Min. 8 characters"
                style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.6rem .85rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '.68rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: '.3rem' }}>Confirm New Password</label>
              <input type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} placeholder="Repeat new password"
                style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '.6rem .85rem', color: 'var(--text)', fontFamily: 'var(--font-body)', fontSize: '.85rem', outline: 'none', boxSizing: 'border-box' }} />
            </div>
            {pwError && <div style={{ fontSize: '.75rem', color: 'var(--danger)', padding: '.5rem .75rem', background: 'rgba(240,80,80,0.08)', borderRadius: 'var(--radius)', border: '1px solid rgba(240,80,80,0.2)' }}>{pwError}</div>}
            {pwMsg && <div style={{ fontSize: '.75rem', color: 'var(--success)', padding: '.5rem .75rem', background: 'rgba(0,200,150,0.08)', borderRadius: 'var(--radius)', border: '1px solid rgba(0,200,150,0.2)' }}>{pwMsg}</div>}
            <button onClick={async () => {
              if (!pwCurrent || !pwNew || !pwConfirm) { setPwError('Please fill in all fields.'); return; }
              if (pwNew !== pwConfirm) { setPwError('New passwords do not match.'); return; }
              if (pwNew.length < 8) { setPwError('Password must be at least 8 characters.'); return; }
              setPwSaving(true); setPwError(''); setPwMsg('');
              try {
                const user = auth.currentUser;
                const credential = EmailAuthProvider.credential(user.email, pwCurrent);
                await reauthenticateWithCredential(user, credential);
                await updatePassword(user, pwNew);
                setPwMsg('Password updated successfully.');
                setPwCurrent(''); setPwNew(''); setPwConfirm('');
                setTimeout(() => setShowPasswordChange(false), 2000);
              } catch (e) {
                if (e.code === 'auth/wrong-password') setPwError('Current password is incorrect.');
                else if (e.code === 'auth/requires-recent-login') setPwError('Please sign out and sign back in before changing your password.');
                else setPwError('Error updating password. Please try again.');
              }
              setPwSaving(false);
            }} disabled={pwSaving} style={{ padding: '.65rem 1.5rem', borderRadius: 'var(--radius)', border: 'none', background: 'var(--primary)', color: '#080f18', fontFamily: 'var(--font-body)', fontSize: '.82rem', fontWeight: 500, cursor: 'pointer', alignSelf: 'flex-start' }}>
              {pwSaving ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        )}
      </div>

      {/* Delete account */}
      <div style={{ marginTop: '3rem', padding: '1.25rem 1.5rem', background: 'rgba(240,80,80,0.04)', border: '1px solid rgba(240,80,80,0.12)', borderRadius: 'var(--radius-lg)' }}>
        <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--danger)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.5rem' }}>Danger Zone</div>
        <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6, marginBottom: '1rem' }}>
          Deleting your account will permanently remove all your health data, training history, nutrition data and profile information. This cannot be undone. Your right to erasure under UK GDPR will be fulfilled within 30 days.
        </p>
        <button onClick={async () => {
          const confirmed = window.confirm('Are you sure you want to delete your account? All your data will be permanently deleted. This cannot be undone.');
          if (!confirmed) return;
          const doubleConfirm = window.confirm('This is your last chance. Delete everything and sign out?');
          if (!doubleConfirm) return;
          try {
            // Delete all user data from Firestore
            const { db } = await import('../firebase');
            const { collection, getDocs, deleteDoc, doc } = await import('firebase/firestore');
            const user = auth.currentUser;
            if (!user) return;
            // Delete latest docs
            const latestSnap = await getDocs(collection(db, 'users', user.uid, 'latest'));
            await Promise.all(latestSnap.docs.map(d => deleteDoc(d.ref)));
            // Delete the user auth account
            await user.delete();
            window.location.reload();
          } catch (e) {
            if (e.code === 'auth/requires-recent-login') {
              alert('For security, please sign out and sign back in before deleting your account.');
            } else {
              alert('Error deleting account: ' + e.message);
            }
          }
        }} style={{ padding: '.6rem 1.25rem', borderRadius: 'var(--radius)', border: '1px solid rgba(240,80,80,.3)', background: 'transparent', color: 'var(--danger)', fontFamily: 'var(--font-body)', fontSize: '.8rem', cursor: 'pointer', fontWeight: 500 }}>
          Delete My Account
        </button>
      </div>

    </div>
  );
}
